import * as React from "react"
import { type match as Match, useHistory, useLocation, useRouteMatch } from "react-router-dom"
import { PageSlideContext } from "@digits-shared/components/PageSlider/PageSlideContext"
import { type DigitsLocation } from "@digits-shared/components/Router/DigitsLocation"
import { type DigitsHistory } from "@digits-shared/components/Router/History"
import useSearchQuery from "@digits-shared/hooks/useSearchQuery"
import { type Location, type LocationState, type Path } from "history"

export interface DigitsUseRouter {
  push: (path: Path, state?: Location | LocationState) => void
  replace: (path: Path, state?: Location | LocationState) => void
  pathname: string
  params: Record<string, string>
  location: DigitsLocation
  previousLocation?: DigitsLocation
  history: DigitsHistory
  match: Match<Record<string, string>>
  search: URLSearchParams
}

/**
 * Returns a custom router object
 */
export default function useRouter() {
  const search = useSearchQuery()
  const history = useHistory() as DigitsHistory
  const { location, previousLocation } = useCurrentLocation()
  const params = useMemoParams<Record<string, string>>(location)
  const match = useMemoRouteMatch<Record<string, string>>(location)

  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  return React.useMemo<DigitsUseRouter>(
    () => ({
      // For convenience add push(), replace(), pathname at top level
      push: history.push,
      replace: history.replace,
      pathname: location.pathname,

      // Include params, match, location, history objects so we have
      // access to extra React Router functionality if needed.
      params,
      match,
      search,
      location,
      previousLocation,
      history,
    }),
    [history, location, params, match, search, previousLocation]
  )
}

/**
 * Hook that determines the current location state, prioritizing the PageSlideContext location
 * when available. This is used to maintain consistent location state during page transitions.
 *
 * The hook returns an object containing:
 * - location: The current location object
 * - previousLocation: The previous location object (if available)
 *
 * During page transitions, the PageSlideContext provides the location state that should be used
 * to ensure animations and routing work correctly. If no PageSlideContext is available,
 * it falls back to the standard history location.
 */
function useCurrentLocation() {
  const { location, previousLocation } = useHistory() as DigitsHistory
  const pageSlideContext = React.useContext(PageSlideContext)
  if (pageSlideContext?.location) {
    return pageSlideContext
  }
  return { location, previousLocation }
}

// WORKAROUND for bug in react router
// https://github.com/ReactTraining/react-router/issues/7059
// useRouteMatch returns a new object on every render, `location.key` is very stable so using that
// to memoize the match record and workaround this bug
function useMemoRouteMatch<Params extends { [K in keyof Params]?: string } = {}>(
  location: DigitsLocation
): Match<Params> {
  // Less accurate location due to our usage of layered routers in webapp. However leveraging it
  // allows up to properly bust the match catch if it changes.
  const loc = useLocation() as DigitsLocation
  const match = useRouteMatch<Params>()

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => match, [location.key, loc.key])
}

// WORKAROUND for bug in react router
// https://github.com/ReactTraining/react-router/issues/7059
function useMemoParams<Params extends { [K in keyof Params]?: string } = {}>(
  location: DigitsLocation
): Params {
  const match = useMemoRouteMatch<Params>(location)
  return React.useMemo(() => match?.params || {}, [match])
}
